import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { siteSettingsService } from '../../services/siteSettingsService';
import MarketingReveal from '../../components/home/MarketingReveal';

const defaultContent = {
  subtitle: 'About us',
  title: 'Welcome to where extraordinary begins',
  content: `VendaList opens doors to verified event vendors — photo booths, photography, entertainment, and more — so you can meet, create, and celebrate without the guesswork.

Our network helps planners fulfill a vision, while vendors earn by listing clear packages and opening their calendars to the right occasions.`,
  ctaText: 'Browse vendors',
  ctaLink: '/browse-vendors',
};

/** PeerSpace-style scattered collage — charcoal field, frames off to the sides */
const COLLAGE = [
  {
    src: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=800&q=80',
    className: 'left-[3%] top-[12%] h-[34%] w-[13%]',
  },
  {
    src: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?auto=format&fit=crop&w=1000&q=80',
    className: 'left-[1%] top-[50%] h-[38%] w-[22%]',
  },
  {
    src: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=800&q=80',
    className: 'bottom-[6%] left-[20%] h-[22%] w-[16%]',
  },
  {
    src: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=900&q=80',
    className: 'right-[6%] top-[10%] h-[28%] w-[20%]',
  },
  {
    src: 'https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=800&q=80',
    className: 'right-[1%] top-[42%] h-[36%] w-[13%]',
  },
  {
    src: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=800&q=80',
    className: 'bottom-[8%] right-[14%] h-[26%] w-[15%]',
  },
];

const MOBILE_COLLAGE = COLLAGE.slice(0, 4).map((f) => f.src);

const HERO_HEADLINE = {
  line1: 'Welcome to where',
  line2: 'extraordinary begins',
};

function resolveHeroHeadline(cmsTitle) {
  const raw = (cmsTitle || '').trim();
  // CMS often stores “About Brand” — keep the collage headline PeerSpace-style.
  if (!raw || /^about\b/i.test(raw)) return HERO_HEADLINE;
  const pivot = ' where ';
  const idx = raw.toLowerCase().indexOf(pivot);
  if (idx === -1) return { line1: raw, line2: null };
  return {
    line1: raw.slice(0, idx + pivot.length).trimEnd(),
    line2: raw.slice(idx + pivot.length).trim(),
  };
}

const AUDIENCES = [
  {
    eyebrow: 'Guests',
    title: 'Find a vendor. Fulfill your vision.',
    description:
      'Meet, create, and celebrate with photo-led listings, clear packages, and reviews — in markets near you.',
    cta: 'Browse vendors',
    href: '/browse-vendors',
  },
  {
    eyebrow: 'Vendors',
    title: 'Your next booking might start here.',
    description:
      'List your booth, crew, or service. Set packages, open availability, and get discovered by planners already searching.',
    cta: 'Become a vendor',
    href: '/vendor-signup',
  },
];

const CONTACT_BLOCKS = [
  {
    title: 'General',
    body: 'Questions about VendaList, partnerships, or the product.',
    linkLabel: 'Contact us',
    href: '/contact',
  },
  {
    title: 'Support',
    body: 'Help with bookings, accounts, or using the marketplace.',
    linkLabel: 'Help center',
    href: '/help',
  },
  {
    title: 'Press',
    body: 'Media kits, announcements, and press inquiries.',
    linkLabel: 'Press room',
    href: '/press',
  },
];

const About = () => {
  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const settings = settingsResponse?.data || {};
  const siteName = settings.site_name || 'VendaList';
  const subtitle = settings.about_subtitle || defaultContent.subtitle;
  const title = settings.about_title || defaultContent.title;
  const content = settings.about_content || defaultContent.content;
  const ctaText = settings.about_cta_text || defaultContent.ctaText;
  const ctaLink = settings.about_cta_link || defaultContent.ctaLink;
  const resolvedCta = ctaLink?.startsWith('/') ? ctaLink : `/${ctaLink || 'browse-vendors'}`;
  const { line1, line2 } = resolveHeroHeadline(title);

  const paragraphs = (content || defaultContent.content)
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      {/* PeerSpace collage hero — charcoal field under solid black header */}
      <section
        className="about-collage-hero relative overflow-hidden"
        style={{ backgroundColor: '#3a3a3a' }}
      >
        <div className="relative mx-auto hidden min-h-[480px] max-w-[1500px] items-center justify-center px-4 py-12 md:flex lg:min-h-[520px]">
          {COLLAGE.map((frame) => (
            <div
              key={frame.src}
              className={`about-collage-frame absolute overflow-hidden ${frame.className}`}
              aria-hidden
            >
              <img
                src={frame.src}
                alt=""
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
          ))}

          <div className="relative z-10 mx-auto max-w-[34rem] px-6 text-center lg:max-w-[40rem]">
            <h1
              className="about-collage-heading text-[clamp(1.75rem,3.2vw,2.5rem)] leading-[1.12] tracking-[-0.025em]"
              style={{ color: '#ffffff', fontWeight: 700 }}
            >
              <span className="block">{line1}</span>
              {line2 ? <span className="block">{line2}</span> : null}
            </h1>
          </div>
        </div>

        {/* Mobile */}
        <div className="px-4 pb-10 pt-6 md:hidden">
          <h1
            className="about-collage-heading mb-8 text-center text-[1.625rem] leading-[1.15] tracking-[-0.025em]"
            style={{ color: '#ffffff', fontWeight: 700 }}
          >
            <span className="block">{line1}</span>
            {line2 ? <span className="block">{line2}</span> : null}
          </h1>
          <div className="grid grid-cols-2 gap-2" aria-hidden>
            {MOBILE_COLLAGE.map((src) => (
              <div key={src} className="aspect-[4/3] overflow-hidden">
                <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission — PeerSpace “We have just the place” */}
      <section className="bg-white px-4 py-20 sm:px-6 sm:py-28">
        <MarketingReveal className="mx-auto max-w-3xl">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-m-ink-faint">
            {subtitle}
          </p>
          <h2
            className="marketing-section-title mb-8 text-m-ink"
            style={{ color: '#0a0a0a' }}
          >
            We have just the vendors
          </h2>
          <div className="space-y-5">
            {paragraphs.map((para) => (
              <p
                key={para.slice(0, 24)}
                className="text-[1.05rem] leading-relaxed sm:text-lg"
                style={{ color: '#6b6b6b' }}
              >
                {para}
              </p>
            ))}
          </div>
          {ctaText ? (
            <div className="mt-10">
              <Link to={resolvedCta} className="marketing-btn marketing-btn-ink gap-2">
                {ctaText}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : null}
        </MarketingReveal>
      </section>

      {/* Guests / Vendors */}
      <section className="border-t border-m-border-light bg-m-canvas-light px-4 py-20 sm:px-6 sm:py-28">
        <div className="mx-auto grid max-w-6xl gap-4 sm:gap-5 lg:grid-cols-2">
          {AUDIENCES.map((item) => (
            <MarketingReveal key={item.eyebrow}>
              <article
                className="flex h-full min-h-[280px] flex-col rounded-2xl bg-white px-7 py-8 sm:px-9 sm:py-10"
                style={{ color: '#0a0a0a' }}
              >
                <p
                  className="mb-4 text-[11px] font-medium uppercase tracking-[0.16em]"
                  style={{ color: '#8a8a8a' }}
                >
                  {item.eyebrow}
                </p>
                <h3
                  className="mb-4 text-2xl font-medium tracking-tight sm:text-3xl"
                  style={{ color: '#0a0a0a' }}
                >
                  {item.title}
                </h3>
                <p
                  className="mb-8 max-w-md text-[15px] leading-relaxed sm:text-base"
                  style={{ color: '#6b6b6b' }}
                >
                  {item.description}
                </p>
                <Link
                  to={item.href}
                  className="marketing-btn marketing-btn-ink mt-auto inline-flex w-fit gap-2"
                >
                  {item.cta}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            </MarketingReveal>
          ))}
        </div>
      </section>

      {/* Join the team — PeerSpace careers band */}
      <section className="bg-m-night px-4 py-20 text-white sm:px-6 sm:py-28">
        <MarketingReveal className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end lg:gap-16">
          <div>
            <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-white/50">
              Careers
            </p>
            <h2 className="marketing-section-title mb-5 max-w-xl text-white">
              Join our team
            </h2>
            <p className="mb-4 max-w-xl text-[1.05rem] leading-relaxed text-white/70">
              Our people are the heart of {siteName}. We build a calm, useful
              marketplace where planners book with confidence and vendors get
              discovered for the right events.
            </p>
            <p className="max-w-xl text-[1.05rem] leading-relaxed text-white/70">
              If that sounds like work you want to do, we’d love to hear from you.
            </p>
            <div className="mt-10">
              <Link to="/careers" className="marketing-btn marketing-btn-ghost gap-2">
                View open roles
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-2xl">
            <img
              src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80"
              alt=""
              className="aspect-[5/4] w-full object-cover"
              loading="lazy"
            />
          </div>
        </MarketingReveal>
      </section>

      {/* Contact columns */}
      <section className="bg-white px-4 py-20 sm:px-6 sm:py-24">
        <MarketingReveal className="mx-auto max-w-6xl">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-m-ink-faint">
            Get in touch
          </p>
          <h2
            className="marketing-section-title mb-12 max-w-xl text-m-ink"
            style={{ color: '#0a0a0a' }}
          >
            We’re here when you need us
          </h2>
          <div className="grid grid-cols-1 gap-10 border-t border-m-border-light pt-10 sm:grid-cols-3 sm:gap-8">
            {CONTACT_BLOCKS.map((block) => (
              <div key={block.title} style={{ color: '#0a0a0a' }}>
                <h3
                  className="mb-3 text-lg font-medium tracking-tight"
                  style={{ color: '#0a0a0a' }}
                >
                  {block.title}
                </h3>
                <p
                  className="mb-5 text-[15px] leading-relaxed"
                  style={{ color: '#6b6b6b' }}
                >
                  {block.body}
                </p>
                <Link
                  to={block.href}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-m-ink underline-offset-4 hover:underline"
                >
                  {block.linkLabel}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </MarketingReveal>
      </section>
    </div>
  );
};

export default About;
